import { useId, useState } from "react";
import { Trash2, AlertTriangle } from "lucide-react";
import * as devicesApi from "../../api/devices";
import { RelazioneCliente } from "../../api/devices";
import { getErrorMessage } from "../../api/client";
import { useToast } from "../../context/ToastContext";
import { Modal } from "../../components/ui/Modal";
import { Button } from "../../components/ui/Button";
import { etichettaMatricola } from "../../utils/matricola";

function dataIT(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString("it-IT") : "—";
}

/**
 * Cancella definitivamente una riga di `device_cliente`.
 *
 * È l'operazione per la riga che NON DOVEVA ESISTERE — il cliente sbagliato
 * preso dall'autocomplete, un doppione dell'import — e non per un rapporto
 * finito davvero: quello si chiude con "Termina rapporto", che è il motivo per
 * cui `data_fine` esiste. Le due azioni stanno vicine nella stessa riga e
 * questo pannello ripete la differenza, perché nel momento in cui uno vuole
 * "togliere questo cliente" sono indistinguibili se non le si spiega.
 *
 * Conferma rafforzata con una spunta, e non il `window.confirm` usato per la
 * DELETE del device: là il backend rifiuta comunque con un 409 se c'è storico
 * collegato, quindi il dialogo del browser è l'ultimo di due filtri. Qui non
 * c'è nessun secondo filtro — nessuna tabella punta a `device_cliente`, la
 * riga sparisce e basta — quindi il filtro deve essere qui.
 */
export function EliminaRelazione({
  open,
  onClose,
  relazione,
  matricola,
  onEliminata,
}: {
  open: boolean;
  onClose: () => void;
  relazione: RelazioneCliente;
  matricola: string | null;
  onEliminata: () => void;
}) {
  const { showToast } = useToast();
  const [capito, setCapito] = useState(false);
  const [inCorso, setInCorso] = useState(false);
  const spuntaId = useId();

  const aperta = relazione.data_fine === null;

  async function conferma() {
    setInCorso(true);
    try {
      await devicesApi.deleteDeviceCliente(relazione.id);
      showToast(`Relazione con ${relazione.cliente.nome} eliminata.`, "success");
      onEliminata();
      onClose();
    } catch (err) {
      showToast(getErrorMessage(err, "Impossibile eliminare la relazione."), "error");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Elimina definitivamente la relazione"
      size="sm"
      footer={
        <div className="flex w-full justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Annulla
          </Button>
          <Button type="button" variant="danger" onClick={conferma} loading={inCorso} disabled={!capito}>
            <Trash2 className="h-4 w-4" aria-hidden="true" />
            Elimina definitivamente
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex gap-3 rounded-lg border border-accent/30 bg-accent/5 p-3">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
          <div className="text-sm leading-relaxed text-ink/70">
            <p>
              Stai per cancellare la riga che collega{" "}
              <span className="font-semibold text-ink">{relazione.cliente.nome}</span> alla cassaforte{" "}
              <span className="font-semibold text-ink">{etichettaMatricola(matricola)}</span>
              {aperta ? " (rapporto in corso)" : ` (rapporto chiuso il ${dataIT(relazione.data_fine)})`}.
            </p>
            <p className="mt-2">
              <span className="font-semibold text-ink">L&apos;operazione non è reversibile.</span> Spariscono anche il
              codice GDO
              {relazione.codice_gdo && <> ({relazione.codice_gdo})</>}, la sede e il servizio di questo periodo, che da
              nessun&apos;altra parte sono scritti.
            </p>
          </div>
        </div>

        <div className="text-sm leading-relaxed text-ink/70">
          <p>
            Da usare solo per una riga <span className="font-semibold text-ink">inserita per errore</span> — il cliente
            sbagliato scelto nell&apos;elenco, un doppione dell&apos;import.
          </p>
          {aperta && (
            <p className="mt-2">
              Se invece il rapporto è finito davvero, chiudilo con{" "}
              <span className="font-semibold text-ink">Termina rapporto</span>: la cassaforte smette di risultare sua e
              lo storico resta leggibile.
            </p>
          )}
          <p className="mt-2">
            {/* Verificato su buildScopedWhere (routes/devices.ts): per un utente
                `cliente` lo scope è `clienti: { some: { cliente_id } }`, senza
                filtro su `data_fine`. È la riga a dare visibilità, aperta o
                chiusa che sia — quindi cancellarla è l'unica delle due azioni
                che toglie davvero il device dalla vista del cliente. */}
            Se {relazione.cliente.nome} ha un accesso all&apos;app e questa è la sua unica riga su questa cassaforte,
            dopo l&apos;eliminazione non la vedrà più: è la riga a dargli visibilità, non il fatto che il rapporto sia
            ancora in corso.
          </p>
        </div>

        <label htmlFor={spuntaId} className="flex cursor-pointer items-start gap-2 text-sm text-ink">
          <input
            id={spuntaId}
            type="checkbox"
            checked={capito}
            onChange={(e) => setCapito(e.target.checked)}
            className="mt-0.5 h-4 w-4 shrink-0 rounded border-line accent-accent"
          />
          <span>Ho capito: la riga sparisce per sempre e non si recupera.</span>
        </label>
      </div>
    </Modal>
  );
}
